import { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Home, Briefcase, X, Check } from 'lucide-react';
import { hereLocationService, savedAddressService, LocationSuggestion, SavedAddress } from '@/services/hereLocationService';
import { useAuthStore } from '@/store/authStore';

interface LocationInputProps {
  value: string;
  onChange: (value: string, coordinates?: { lat: number; lng: number }, suggestion?: LocationSuggestion) => void;
  placeholder?: string;
  className?: string;
  showSavedAddresses?: boolean;
  showCurrentLocation?: boolean;
  autoFocus?: boolean;
}

export function LocationInput({
  value,
  onChange,
  placeholder = "Enter your location",
  className = "",
  showSavedAddresses = true,
  showCurrentLocation = true,
  autoFocus = false,
}: LocationInputProps) {
  const { isAuthenticated } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [pendingAddress, setPendingAddress] = useState<{ address: string; coordinates: { lat: number; lng: number } } | null>(null);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Load saved addresses on mount (only if authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      setSavedAddresses(savedAddressService.getSavedAddresses());
    } else {
      setSavedAddresses([]);
    }
  }, [isAuthenticated]);

  // Handle input changes with debouncing
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Don't trim the value here - preserve spaces for better search
    const searchValue = value;
    
    if (searchValue.length >= 2) { // Reduced from 3 to 2 characters
      debounceRef.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          console.log('🔍 Searching for:', `"${searchValue}"`, 'Length:', searchValue.length);
          const results = await hereLocationService.getAddressSuggestions(searchValue, {
            countryCode: 'IRL',
            limit: 6,
          });
          console.log('📥 Got suggestions:', results);
          setSuggestions(results);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          // Fallback: show a basic suggestion based on user input
          setSuggestions([{
            id: 'manual_input',
            title: searchValue,
            address: searchValue,
            coordinates: { lat: 53.3498, lng: -6.2603 }, // Default to Dublin
            type: 'suggestion',
          }]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log('🔤 LocationInput value changed:', newValue, 'Length:', newValue.length);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleSuggestionClick = (suggestion: LocationSuggestion) => {
    onChange(suggestion.address, suggestion.coordinates, suggestion);
    setIsOpen(false);
    
    // Only offer to save if user is authenticated and it's a new suggestion (not saved)
    if (isAuthenticated && suggestion.type === 'suggestion') {
      setPendingAddress({
        address: suggestion.address,
        coordinates: suggestion.coordinates,
      });
      
      // Set default label to "Home" if it's the first address
      const defaultLabel = savedAddresses.length === 0 ? 'Home' : '';
      setSaveLabel(defaultLabel);
      
      setShowSaveDialog(true);
    }
  };

  const handleSavedAddressClick = (address: SavedAddress) => {
    onChange(address.address, address.coordinates);
    setIsOpen(false);
  };

  const handleCurrentLocation = async () => {
    setIsLoading(true);
    try {
      // First try to get coordinates
      const coords = await hereLocationService.getCurrentLocation();
      if (!coords) {
        alert('Unable to access your location. Please check your browser permissions and try again.');
        return;
      }

      // Try to get address from coordinates
      const location = await hereLocationService.getCurrentLocationWithAddress();
      if (location) {
        // Pass the full location object as the suggestion parameter so structured address is available
        onChange(location.address, location.coordinates, location);
        setIsOpen(false);
      } else {
        // Fallback: use coordinates without address
        onChange(`Location: ${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`, coords);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      alert('Error getting your current location. Please try typing your address instead.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAddress = () => {
    if (!pendingAddress || !saveLabel.trim()) return;

    savedAddressService.saveAddress({
      label: saveLabel.trim(),
      address: pendingAddress.address,
      coordinates: pendingAddress.coordinates,
      isDefault: savedAddresses.length === 0, // First address becomes default
    });

    setSavedAddresses(savedAddressService.getSavedAddresses());
    setShowSaveDialog(false);
    setSaveLabel('');
    setPendingAddress(null);
  };

  const handleCancelSave = () => {
    setShowSaveDialog(false);
    setSaveLabel('');
    setPendingAddress(null);
  };

  const getSuggestedLabels = () => {
    const existingLabels = savedAddresses.map(addr => addr.label.toLowerCase());
    const suggestions = ['Home', 'Work', 'Office'];
    return suggestions.filter(label => !existingLabels.includes(label.toLowerCase()));
  };

  return (
    <div className="relative">
      {/* Main Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={`form-input pl-10 ${className}`}
        />
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-medium" />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="loading-spinner w-4 h-4 border-primary-600"></div>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-neutral-light rounded-lg shadow-lg max-h-80 overflow-y-auto"
        >
          {/* Current Location Button */}
          {showCurrentLocation && (
            <button
              onClick={handleCurrentLocation}
              disabled={isLoading}
              className="w-full px-4 py-3 text-left hover:bg-neutral-light flex items-center space-x-3 border-b border-neutral-light disabled:opacity-50"
            >
              <Navigation className="w-4 h-4 text-primary-600" />
              <div>
                <div className="font-medium text-neutral-dark">Use My Location</div>
                <div className="text-caption text-neutral-medium">Get current location automatically</div>
              </div>
            </button>
          )}

          {/* Saved Addresses - Only show when authenticated */}
          {isAuthenticated && showSavedAddresses && savedAddresses.length > 0 && (
            <div className="border-b border-gray-100">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Saved Addresses
              </div>
              {savedAddresses.map((address) => (
                <button
                  key={address.id}
                  onClick={() => handleSavedAddressClick(address)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3"
                >
                  {address.label.toLowerCase() === 'home' ? (
                    <Home className="w-4 h-4 text-green-600" />
                  ) : address.label.toLowerCase() === 'work' || address.label.toLowerCase() === 'office' ? (
                    <Briefcase className="w-4 h-4 text-purple-600" />
                  ) : (
                    <MapPin className="w-4 h-4 text-gray-600" />
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 flex items-center">
                      {address.label}
                      {address.isDefault && (
                        <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 truncate">{address.address}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div>
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                Suggestions
              </div>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3"
                >
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{suggestion.title}</div>
                    <div className="text-sm text-gray-500">{suggestion.address}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No results */}
          {!isLoading && value.length >= 2 && suggestions.length === 0 && (
            <div className="px-4 py-6 text-center text-gray-500">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <div className="text-sm">No locations found for "{value}"</div>
              <div className="text-xs text-gray-400 mt-1">
                Try typing more of the address or check spelling
              </div>
              <div className="text-xs text-blue-600 mt-2">
                💡 Tip: Include house number and street name (e.g., "92 Summerhill Lane")
              </div>
            </div>
          )}

          {/* Loading state */}
          {isLoading && value.length >= 2 && (
            <div className="px-4 py-6 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <div className="text-sm">Searching locations...</div>
            </div>
          )}
        </div>
      )}

      {/* Save Address Dialog - Only show when authenticated */}
      {isAuthenticated && showSaveDialog && pendingAddress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Save Address</h3>
              <button
                onClick={handleCancelSave}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-2">Address:</div>
              <div className="text-sm font-medium text-gray-900 bg-gray-50 p-3 rounded-lg">
                {pendingAddress.address}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Save as:
              </label>
              <input
                type="text"
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                placeholder="e.g., Home, Work, Office"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                autoFocus
              />
            </div>

            {/* Quick label suggestions */}
            <div className="mb-6">
              <div className="text-xs text-gray-500 mb-2">Quick options:</div>
              <div className="flex flex-wrap gap-2">
                {getSuggestedLabels().map((label) => (
                  <button
                    key={label}
                    onClick={() => setSaveLabel(label)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelSave}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleSaveAddress}
                disabled={!saveLabel.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <Check className="w-4 h-4 mr-1" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}